import { Checkbox, ContentLayout, FormField, Header, Input, SpaceBetween, Tabs } from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import AbvCalculator from '../components/AbvCalculator';
import HoneyCalculator from '../components/HoneyCalculator';
import StackedBottleSlider from '../components/MultiBottleSlider';
import { useAuthContext } from '../contexts/AuthContext';
import useSubscribe from '../hooks/useSubscribe';
import { useModuleTranslation } from '../lang';
import { InventoryItem } from '../models/Inventory';
import QR from '../tools/QR';
import { prefixCollection } from '../utils/prefix';

const Tools = () => {
  const [liters, setLiters] = useState('100'); // Example initial value
  const [bottleSizes, setBottleSizes] = useState<number[]>([]);
  const [containers, setContainers] = useState<InventoryItem[]>([]);
  const [selectedContainerIds, setSelectedContainerIds] = useState<string[]>([]);
  const [, setError] = useState<Error>();
  const t = useModuleTranslation('Tools');
  const { tenant } = useAuthContext();

  useSubscribe<InventoryItem>(
    prefixCollection('inventory', tenant),
    (items) => {
      const containerItems = items.filter((i) => i.isContainer && typeof i.capacityL === 'number');
      setContainers(containerItems);
    },
    setError,
    (a, b) => (a.capacityL ?? 0) - (b.capacityL ?? 0),
    [{ field: 'isContainer', operator: '==', value: true }],
  );

  useEffect(() => {
    if (selectedContainerIds.length === 0 && containers.length > 0) {
      setSelectedContainerIds(containers.map((c) => c.id));
    }
  }, [containers]);

  useEffect(() => {
    const sizes = selectedContainerIds
      .map((id) => containers.find((c) => c.id === id)?.capacityL)
      .filter((cap): cap is number => typeof cap === 'number');
    setBottleSizes(sizes);
  }, [selectedContainerIds, containers]);

  const handleLitersChange = (value: string) => {
    setLiters(value);
  };

  return (
    <ContentLayout disableOverlap header={<Header variant="h1">{t.header}</Header>}>
      <Tabs
        tabs={[
          {
            label: t.tabs.qr,
            id: 'qr',
            content: <QR />,
          },
          {
            label: t.tabs.abv,
            id: 'abv',
            content: <AbvCalculator />,
          },
          {
            label: t.tabs.honey,
            id: 'honey',
            content: <HoneyCalculator />,
          },
          {
            label: t.tabs.bottleSlider,
            id: 'bottle-slider',
            content: (
              <SpaceBetween size="l">
                <FormField label={t.totalLiters}>
                  <Input
                    type="number"
                    placeholder={t.placeholderLiters}
                    value={liters}
                    onChange={({ detail }) => handleLitersChange(detail.value)}
                  />
                </FormField>
                <FormField label={t.bottleSizes}>
                  <SpaceBetween size="xs">
                    {containers.map((c) => (
                      <Checkbox
                        key={c.id}
                        checked={selectedContainerIds.includes(c.id)}
                        onChange={({ detail }) => {
                          const checked = detail.checked;
                          setSelectedContainerIds((prev) =>
                            checked ? [...prev, c.id] : prev.filter((id) => id !== c.id),
                          );
                        }}
                      >
                        {c.name} ({c.capacityL?.toFixed(3).replace(/\.0+$/, '')}L)
                      </Checkbox>
                    ))}
                  </SpaceBetween>
                </FormField>
                <p>{t.adjustSlider}</p>
                <StackedBottleSlider totalLiters={parseFloat(liters)} sizes={bottleSizes} />
              </SpaceBetween>
            ),
          },
        ]}
      />
    </ContentLayout>
  );
};

export default Tools;
